import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Tooltip } from "react-tooltip";
import { getPropertyCountByStateAction, getPropertyCountByDistrictAction } from "../../../../store/actions/Report/ReportActions";

// India state GeoJSON
import indiaGeoJson from "../../../../data/india_states.json";

// Color scale function
const colorScale = (value, min, max) => {
  if (value === 0) return "#E6E6E6";
  const ratio = (value - min) / (max - min || 1);
  const startColor = [198, 219, 239];
  const endColor = [8, 48, 107];
  const r = Math.round(startColor[0] + ratio * (endColor[0] - startColor[0]));
  const g = Math.round(startColor[1] + ratio * (endColor[1] - startColor[1]));
  const b = Math.round(startColor[2] + ratio * (endColor[2] - startColor[2]));
  return `rgb(${r},${g},${b})`;
};

const IndiaMapChart = () => {
  const dispatch = useDispatch();
  const [selectedState, setSelectedState] = useState(null);
  const [districtGeoJson, setDistrictGeoJson] = useState(null);
  const { stateDataMap, districtDataMap } = useSelector((state) => state.report);

  // Load all-district GeoJSON from public folder
  useEffect(() => {
    fetch("/data/india_district.geojson")
      .then((res) => res.json())
      .then((data) => setDistrictGeoJson(data))
      .catch((err) => console.error("Error loading district GeoJSON:", err));
  }, []);

  useEffect(() => {
    dispatch(getPropertyCountByStateAction());
    dispatch(getPropertyCountByDistrictAction());
  }, [dispatch]);

  // Filter district GeoJSON for selected state
  const districtGeoData = useMemo(() => {
    if (!selectedState || !districtGeoJson?.features) return null;
    const filtered = districtGeoJson.features.filter(
      (d) =>
        d.properties.NAME_1 &&
        d.properties.NAME_1.trim().toLowerCase() ===
          selectedState.trim().toLowerCase()
    );
    return { type: "FeatureCollection", features: filtered };
  }, [selectedState, districtGeoJson]);

  // Data & geo to render
  // For districts, always return an object with all district names as keys, defaulting to 0 if not present in districtDataMap
  const mapData = useMemo(() => {
    if (!selectedState) return stateDataMap;
    if (!districtGeoJson?.features) return {};
    const districts = districtGeoJson.features
      .filter(
        (d) =>
          d.properties.NAME_1 &&
          d.properties.NAME_1.trim().toLowerCase() ===
            selectedState.trim().toLowerCase()
      )
      .map((d) => d.properties.NAME_2);
    const data = {};
    districts.forEach((name) => {
      data[name] =
        districtDataMap[selectedState] &&
        districtDataMap[selectedState][name] !== undefined
          ? districtDataMap[selectedState][name]
          : 0;
    });
    return data;
  }, [selectedState, districtGeoJson]);
  // Always provide a valid FeatureCollection for geoData
  const geoData = useMemo(() => {
    if (!selectedState) return indiaGeoJson;
    if (
      districtGeoData &&
      districtGeoData.features &&
      districtGeoData.features.length > 0
    ) {
      return districtGeoData;
    }
    // If no districts found, fallback to an empty FeatureCollection to avoid rendering errors
    return { type: "FeatureCollection", features: [] };
  }, [selectedState, districtGeoData]);

  const { min, max } = useMemo(() => {
    const values = Object.values(mapData);
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [mapData]);

  if (selectedState && !districtGeoData) {
    return <div>Loading {selectedState} districts...</div>;
  }
  if (
    selectedState &&
    districtGeoData &&
    districtGeoData.features.length === 0
  ) {
    return (
      <div>
        No district data found for {selectedState}. Please check the state name
        or district GeoJSON.
      </div>
    );
  }

  // Calculate dynamic center and scale for selected state's districts
  const getDistrictBounds = (features) => {
    if (!features || features.length === 0) return null;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    features.forEach((f) => {
      const coords = f.geometry.coordinates;
      // Handle MultiPolygon and Polygon
      const polygons =
        f.geometry.type === "MultiPolygon" ? coords.flat(1) : coords;
      polygons.forEach((ring) => {
        ring.forEach(([x, y]) => {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        });
      });
    });
    return { minX, minY, maxX, maxY };
  };

  let projectionConfig = { scale: 1000, center: [82.8, 22.5] };
  if (selectedState && geoData.features && geoData.features.length > 0) {
    const bounds = getDistrictBounds(geoData.features);
    if (bounds) {
      // Center is the midpoint of bounds
      projectionConfig.center = [
        (bounds.minX + bounds.maxX) / 2,
        (bounds.minY + bounds.maxY) / 2,
      ];
      // Scale based on bounds size (increase factor for better fit)
      const size = Math.max(
        bounds.maxX - bounds.minX,
        bounds.maxY - bounds.minY
      );
      projectionConfig.scale = 30000 / (size || 1); // Increased from 15000 to 30000
    }
  }

  return (
    <div className="col-xl-12 col-xxl-12">
      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-header border-0 pb-0">
              <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
                {selectedState
                  ? `${selectedState} Properties by District`
                  : "Properties by State"}
              </h3>
            </div>
            <div className="card-body">                                            
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minHeight: "500px",
                }}
              >
                {selectedState && (
                  <button
                    onClick={() => setSelectedState(null)}
                    style={{ marginBottom: "10px" }}
                  >
                    ← Back to India Map
                  </button>
                )}

                <div
                  style={{
                    width: "50%",
                    height: "500px",
                    position: "relative",
                    marginBottom: "70px",
                  }}
                >
                  <ComposableMap
                    projection="geoMercator"
                    projectionConfig={projectionConfig}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Geographies geography={geoData}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          // Use NAME_1 for state, NAME_2 for district
                          const name = selectedState
                            ? geo.properties.NAME_2 // district name
                            : geo.properties.NAME_1; // state name
                          // Always use 0 if no property count data for district
                          const value =
                            mapData[name] !== undefined ? mapData[name] : 0;
                          let fillColor = colorScale(value, min, max);

                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              data-tooltip-id="map-tooltip"
                              data-tooltip-content={`${name}: ${value} properties`}
                              onClick={() => {
                                if (!selectedState && geo.properties.NAME_1) {
                                  setSelectedState(geo.properties.NAME_1);
                                }
                              }}
                              style={{
                                default: { fill: fillColor, outline: "none" },
                                hover: {
                                  fill: "#F53",
                                  outline: "none",
                                  cursor: "pointer",
                                },
                                pressed: { fill: "#E42", outline: "none" },
                              }}
                            />
                          );
                        })
                      }
                    </Geographies>
                  </ComposableMap>

                  {/* Legend */}
                  <div className="mt-4 w-80">
                    <div
                      style={{
                        height: "14px",
                        background: `linear-gradient(to right, ${colorScale(
                          min,
                          min,
                          max
                        )}, ${colorScale(max, min, max)})`,
                        borderRadius: "6px",
                        position: "relative",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          top: "18px",
                          left: "0",
                          fontSize: "12px",
                          color: "#555",
                        }}
                      >
                        {min} properties
                      </span>
                      <span
                        style={{
                          position: "absolute",
                          top: "18px",
                          right: "0",
                          fontSize: "12px",
                          color: "#555",
                        }}
                      >
                        {max} properties
                      </span>
                    </div>
                  </div>

                  <Tooltip id="map-tooltip" />
                </div>
              </div>
            </div> 
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndiaMapChart;
